import React, { useMemo } from 'react';
import { Table, Button, Space, Card, Select, Input, Tag, Popconfirm, Row, Col, Statistic, Alert, Radio, Empty, Badge, Typography, Tooltip } from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined, ReloadOutlined,
  IdcardOutlined, ClusterOutlined, UsergroupAddOutlined, UserOutlined,
  AppstoreOutlined, UnorderedListOutlined, SendOutlined
} from '@ant-design/icons';

const { Option } = Select;
const { Text } = Typography;

export default function BiometricMappingTab({
  mappings,
  mapLoading,
  clusterFilter,
  setClusterFilter,
  ayFilter,
  setAyFilter,
  standardFilter,
  setStandardFilter,
  combinationFilter = 'all',
  setCombinationFilter,
  availableCombinations = [],
  mapSearch,
  setMapSearch,
  mappingViewMode,
  setMappingViewMode,
  availableClusters,
  academicYears,
  availableStandards,
  isStudio = false,
  onReload,
  onDownloadTemplate,
  onOpenBulkModal,
  onOpenImportModal,
  onOpenPushModal,
  onDeleteAllMappings,
  onOpenMapModal,
  onDeleteMap,
  onPushSingleUser
}) {
  // Statistics
  const mappingStats = useMemo(() => {
    const totalMapped = mappings.length;
    const clusterSet = new Set();
    let candidateMappings = 0;
    let staffMappings = 0;

    mappings.forEach(m => {
      if (m.cluster_name) clusterSet.add(m.cluster_name);
      if (m.person_type === 'CANDIDATE') candidateMappings++;
      else if (m.person_type === 'STAFF') staffMappings++;
    });

    return {
      totalMapped,
      activeClusters: clusterSet.size,
      candidateMappings,
      staffMappings
    };
  }, [mappings]);

  // Filtered Mappings
  const filteredMappings = useMemo(() => {
    return mappings.filter(m => {
      if (clusterFilter !== 'all' && String(m.cluster_id) !== String(clusterFilter)) return false;
      if (ayFilter !== 'all' && String(m.academic_year_id) !== String(ayFilter)) return false;
      if (standardFilter !== 'all' && String(m.standard_id) !== String(standardFilter)) return false;
      if (combinationFilter && combinationFilter !== 'all' && m.person_type === 'CANDIDATE' && m.combination !== combinationFilter) return false;
      if (!mapSearch) return true;
      const q = mapSearch.toLowerCase();
      const name = String(m.staff_name || m.candidate_name || '').toLowerCase();
      const bioId = String(m.biometric_user_id || '').toLowerCase();
      const cluster = String(m.cluster_name || '').toLowerCase();
      const std = String(m.standard_name || '').toLowerCase();
      const comb = String(m.combination || '').toLowerCase();
      return name.includes(q) || bioId.includes(q) || cluster.includes(q) || std.includes(q) || comb.includes(q);
    });
  }, [mappings, clusterFilter, ayFilter, standardFilter, combinationFilter, mapSearch]);

  // Cluster-Wise Groups
  const clusterWiseGroups = useMemo(() => {
    const map = new Map();
    filteredMappings.forEach(m => {
      const key = m.cluster_id ? String(m.cluster_id) : 'unassigned';
      if (!map.has(key)) {
        map.set(key, {
          cluster_id: m.cluster_id || null,
          cluster_name: m.cluster_name || 'General / Unassigned Candidates & Staff',
          cluster_code: m.cluster_code || 'N/A',
          mappings: [],
          candidateCount: 0,
          staffCount: 0
        });
      }
      const grp = map.get(key);
      grp.mappings.push(m);
      if (m.person_type === 'CANDIDATE') grp.candidateCount++;
      else if (m.person_type === 'STAFF') grp.staffCount++;
    });
    return Array.from(map.values()).sort((a, b) => a.cluster_name.localeCompare(b.cluster_name));
  }, [filteredMappings]);

  // Columns definition
  const mappingColumns = [
    {
      title: 'Biometric ID',
      dataIndex: 'biometric_user_id',
      key: 'biometric_user_id',
      render: (id) => <Tag color="geekblue" style={{ fontSize: 13, padding: '2px 8px' }}>ID: {id}</Tag>
    },
    {
      title: 'Badge / Card No.',
      dataIndex: 'badge_number',
      key: 'badge_number',
      render: (num) => num ? <Tag color="cyan" icon={<IdcardOutlined />}>{num}</Tag> : <Text type="secondary">-</Text>
    },
    {
      title: 'Device Role',
      dataIndex: 'device_role',
      key: 'device_role',
      render: (role) => {
        if (role === 'ADMIN') return <Tag color="volcano">Administrator</Tag>;
        if (role === 'MANAGER') return <Tag color="purple">Manager</Tag>;
        return <Tag color="blue">Normal User</Tag>;
      }
    },
    {
      title: 'Cluster',
      key: 'cluster',
      render: (_, record) => {
        if (!record.cluster_name) return <Tag color="default">General / Unassigned</Tag>;
        return (
          <Tag color="purple" icon={<ClusterOutlined />} style={{ padding: '2px 8px', fontSize: 12, fontWeight: 500 }}>
            {record.cluster_name} {record.cluster_code ? `(${record.cluster_code})` : ''}
          </Tag>
        );
      }
    },
    {
      title: 'Assignee Type',
      dataIndex: 'person_type',
      key: 'person_type',
      render: (type) => (
        <Tag color={type === 'STAFF' ? 'blue' : 'magenta'}>
          {type}
        </Tag>
      )
    },
    {
      title: 'Assigned Individual Name',
      key: 'assigned_name',
      render: (_, record) => {
        const isStaff = record.person_type === 'STAFF';
        const name = isStaff ? record.staff_name : record.candidate_name;
        const sub = isStaff ? `Code: ${record.staff_code || '-'}` : `Candidate ID: ${record.candidate_id}`;
        return (
          <div>
            <strong>{name || 'Unknown Assignee'}</strong>
            <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{sub}</div>
          </div>
        );
      }
    },
    {
      title: 'Academic Year',
      dataIndex: 'academic_year',
      key: 'academic_year',
      render: (ay) => ay || 'All Years'
    },
    {
      title: 'Standard / Combination',
      key: 'std_comb',
      render: (_, record) => {
        if (record.person_type === 'STAFF') return <span style={{ color: '#bfbfbf' }}>-</span>;
        return (
          <Space size={4} wrap>
            {record.standard_name && <Tag color="cyan">{record.standard_name}</Tag>}
            {record.combination && <Tag color="geekblue">{record.combination}</Tag>}
            {!record.standard_name && !record.combination && <span style={{ color: '#bfbfbf' }}>-</span>}
          </Space>
        );
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (st) => <Badge status={st === 'Active' ? 'success' : 'default'} text={st} />
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="middle">
          <Tooltip title="Push User to Device">
            <Button
              type="text"
              icon={<SendOutlined style={{ color: 'var(--primary-color)' }} />}
              onClick={() => onPushSingleUser(record.mapping_id)}
            />
          </Tooltip>
          <Tooltip title="Edit Mapping">
            <Button
              type="text"
              icon={<EditOutlined style={{ color: 'var(--primary-color)' }} />}
              onClick={() => onOpenMapModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this biometric user mapping?"
            onConfirm={() => onDeleteMap(record.mapping_id)}
            okText="Delete"
            cancelText="Cancel"
            okButtonProps={{ danger: true }}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <Alert
        message="Cluster-Wise Biometric Device User Mappings"
        description="View and manage eSSL K30 Biometric User IDs grouped Cluster-Wise. Each physical biometric punch is mapped to candidate or staff profiles registered within specific clusters."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Overview Metric Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Total Mapped Users" value={mappingStats.totalMapped} prefix={<IdcardOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Mapped Clusters" value={mappingStats.activeClusters} valueStyle={{ color: '#722ed1' }} prefix={<ClusterOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Candidate Mappings" value={mappingStats.candidateMappings} valueStyle={{ color: '#eb2f96' }} prefix={<UsergroupAddOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card bodyStyle={{ padding: 16 }} style={{ background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
            <Statistic title="Staff Mappings" value={mappingStats.staffMappings} valueStyle={{ color: '#13c2c2' }} prefix={<UserOutlined />} />
          </Card>
        </Col>
      </Row>

      {/* Filters & Control Toolbar */}
      <Card bodyStyle={{ padding: 16 }} style={{ marginBottom: 16, background: 'var(--panel-color)', borderColor: 'var(--border-color)', borderRadius: 8 }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col xs={24} sm={18} md={16}>
            <Space wrap>
              <Select
                value={ayFilter}
                onChange={(val) => {
                  setAyFilter(val);
                  setClusterFilter('all');
                  setStandardFilter('all');
                  if (setCombinationFilter) setCombinationFilter('all');
                }}
                style={{ width: 160 }}
                placeholder="Select Academic Year"
              >
                <Option value="all">All Academic Years</Option>
                {academicYears.map(ay => (
                  <Option key={ay.academic_year_id} value={String(ay.academic_year_id)}>
                    {ay.academic_year}
                  </Option>
                ))}
              </Select>

              <Select
                value={clusterFilter}
                onChange={(val) => {
                  setClusterFilter(val);
                  setStandardFilter('all');
                  if (setCombinationFilter) setCombinationFilter('all');
                }}
                style={{ width: 170 }}
                suffixIcon={<ClusterOutlined />}
              >
                {!isStudio && <Option value="all">All Clusters</Option>}
                {availableClusters.map(c => (
                  <Option key={c.cluster_id} value={String(c.cluster_id)}>
                    {c.cluster_name} {c.cluster_code ? `(${c.cluster_code})` : ''}
                  </Option>
                ))}
              </Select>

              <Select
                value={standardFilter}
                onChange={(val) => {
                  setStandardFilter(val);
                  if (setCombinationFilter) setCombinationFilter('all');
                }}
                style={{ width: 140 }}
              >
                <Option value="all">All Standards</Option>
                {availableStandards.map(st => (
                  <Option key={st.standard_id} value={String(st.standard_id)}>
                    {st.standard_name}
                  </Option>
                ))}
              </Select>

              <Select
                value={combinationFilter || 'all'}
                onChange={setCombinationFilter}
                style={{ width: 150 }}
                placeholder="Combination"
              >
                <Option value="all">All Combinations</Option>
                {(availableCombinations || []).map(comb => (
                  <Option key={comb} value={comb}>
                    {comb}
                  </Option>
                ))}
              </Select>

              <Input
                placeholder="Search Bio ID, Name, Cluster..."
                prefix={<SearchOutlined />}
                value={mapSearch}
                onChange={(e) => setMapSearch(e.target.value)}
                style={{ width: 180 }}
                allowClear
              />
            </Space>
          </Col>

          <Col xs={24} sm={8} md={10} style={{ textAlign: 'right' }}>
            <Space wrap>
              <Radio.Group
                value={mappingViewMode}
                onChange={(e) => setMappingViewMode(e.target.value)}
                optionType="button"
                buttonStyle="solid"
              >
                <Radio.Button value="cluster-wise"><AppstoreOutlined /> Cluster Wise</Radio.Button>
                <Radio.Button value="table"><UnorderedListOutlined /> Flat Table</Radio.Button>
              </Radio.Group>

              <Button icon={<ReloadOutlined />} onClick={onReload} loading={mapLoading} />
              <Button type="primary" ghost icon={<UsergroupAddOutlined />} onClick={onOpenImportModal}>
                Import Existing Users
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenMapModal()}>
                Add Mapping
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* View rendering */}
      {mappingViewMode === 'cluster-wise' ? (
        clusterWiseGroups.length === 0 ? (
          <Card style={{ background: 'var(--panel-color)', textAlign: 'center', padding: 40 }}>
            <Empty description="No mapped biometric users found for the selected cluster filters" />
          </Card>
        ) : (
          <Row gutter={[16, 16]}>
            {clusterWiseGroups.map(group => (
              <Col xs={24} key={group.cluster_id || 'unassigned'}>
                <Card
                  style={{
                    background: 'var(--panel-color)',
                    borderColor: 'var(--border-color)',
                    borderRadius: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                  }}
                  title={
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space align="center" size="middle">
                        <ClusterOutlined style={{ fontSize: 20, color: '#722ed1' }} />
                        <div>
                          <strong style={{ fontSize: 16, color: 'var(--text-color)' }}>{group.cluster_name}</strong>
                          {group.cluster_code !== 'N/A' && (
                            <Tag color="purple" style={{ marginLeft: 8 }}>Code: {group.cluster_code}</Tag>
                          )}
                        </div>
                      </Space>
                      <Space wrap>
                        <Tag color="geekblue" style={{ fontSize: 12, padding: '2px 10px', borderRadius: 4 }}>
                          Total: {group.mappings.length}
                        </Tag>
                        <Tag color="pink" style={{ fontSize: 12, padding: '2px 10px', borderRadius: 4 }}>
                          Candidates: {group.candidateCount}
                        </Tag>
                        <Tag color="cyan" style={{ fontSize: 12, padding: '2px 10px', borderRadius: 4 }}>
                          Staff: {group.staffCount}
                        </Tag>
                      </Space>
                    </Space>
                  }
                >
                  <Table
                    columns={mappingColumns.filter(col => col.key !== 'cluster')}
                    dataSource={group.mappings}
                    rowKey="mapping_id"
                    pagination={group.mappings.length > 50 ? { pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200', '500'] } : false}
                    size="middle"
                    bordered
                    scroll={{ x: 'max-content' }}
                  />
                </Card>
              </Col>
            ))}
          </Row>
        )
      ) : (
        <Table
          columns={mappingColumns}
          dataSource={filteredMappings}
          rowKey="mapping_id"
          loading={mapLoading}
          pagination={{ pageSize: 50, showSizeChanger: true, pageSizeOptions: ['20', '50', '100', '200', '500'] }}
          bordered
          scroll={{ x: 'max-content' }}
          style={{ background: 'var(--panel-color)' }}
        />
      )}
    </div>
  );
}
